import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { withApiPrefix, resolveCoverUrl } from '../../services/api';
import styles from './StudySentencesPage.module.css';
import { AlertIcon, EditIcon } from '../../components/Icons';
import LoadingSpinner from '../../components/LoadingSpinner';
import StudyHeader from './StudyHeader';

const StudySentencesPage = () => {
  const navigate = useNavigate();

  const [sentenceGroups, setSentenceGroups] = useState([]);
  const [bookMetadata, setBookMetadata] = useState({});
  const [sentenceGroupsLoading, setSentenceGroupsLoading] = useState(true);
  const [sentenceGroupsError, setSentenceGroupsError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const sentenceCount = useMemo(
    () => sentenceGroups.reduce((acc, group) => acc + (group.itemCount || 0), 0),
    [sentenceGroups]
  );

  const fetchSentenceGroupsSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setSentenceGroupsError(null);
      setSentenceGroupsLoading(true);

      const response = await api.get(withApiPrefix('favorite-sentences/summary'), {
        params: { limit: 50 },
      });
      const groups = Array.isArray(response.data?.groups) ? response.data.groups : [];
      const normalized = groups.map((group) => ({
        key: String(group.book_id ?? group.bookId ?? 'unknown'),
        bookId: group.book_id ?? group.bookId ?? null,
        bookTitle: group.book_title || group.bookTitle || '未命名书籍',
        itemCount: group.item_count ?? group.itemCount ?? 0,
      }));

      setSentenceGroups(normalized);
      // 预取书籍封面
      const ids = normalized
        .map((g) => g.bookId)
        .filter((id) => id !== null && id !== undefined)
        .map((id) => String(id));
      const uniqueIds = Array.from(new Set(ids));
      if (uniqueIds.length) {
        const responses = await Promise.allSettled(
          uniqueIds.map(async (id) => {
            const res = await api.get(`/novels/${id}`);
            return { id, data: res.data };
          })
        );
        const meta = {};
        responses.forEach((r) => {
          if (r.status === 'fulfilled') {
            const data = r.value.data || {};
            meta[r.value.id] = {
              ...data,
              cover_image_url: resolveCoverUrl(data.cover_image_url),
            };
          }
        });
        setBookMetadata(meta);
      }
    } catch (err) {
      console.error('获取收藏句子统计失败:', err);
      setSentenceGroupsError('获取收藏句子失败，请稍后重试');
      setError('获取收藏句子失败，请稍后重试');
    } finally {
      setSentenceGroupsLoading(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSentenceGroupsSummary().then(() => {
      setLoading(false);
    });
  }, [fetchSentenceGroupsSummary]);

  const handleStartSentenceStudy = useCallback(
    (bookId) => {
      if (!bookId) return;
      navigate(`/learn/sentences?bookid=${encodeURIComponent(bookId)}`);
    },
    [navigate]
  );

  if (loading) {
    return (
      <div className={styles.study}>
        <LoadingSpinner fullPage text="加载收藏内容中..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.study}>
        <div className={styles.noHistory}>
          <div className={styles.noHistoryIcon}><AlertIcon size={48} /></div>
          <h3>无法加载收藏内容</h3>
          <p>{error}</p>
          <button
            className={styles.retryBtn}
            onClick={() => window.location.reload()}
          >
            重试
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.study}>
      <div className={styles.detailView}>
        <StudyHeader title="收藏句子" onBack={() => navigate('/study')} />
        <div className={styles.detailContent}>
          <div className={styles.bookList}>
            {sentenceGroups.map((group) => (
              <div
                key={group.key}
                className={styles.bookItem}
                onClick={() => {
                  if (!group.bookId) return;
                  navigate(`/study/sentences?bookid=${encodeURIComponent(group.bookId)}`);
                }}
              >
                <div className={styles.bookCover}>
                  {bookMetadata[group.bookId]?.cover_image_url ? (
                    <img
                      src={bookMetadata[group.bookId].cover_image_url}
                      alt={group.bookTitle}
                      loading="lazy"
                    />
                  ) : (
                    <div className={styles.bookCoverPlaceholder}>📖</div>
                  )}
                </div>
                <div className={styles.bookInfo}>
                  <span className={styles.bookTabTitle}>{group.bookTitle}</span>
                  <span className={styles.bookTabCount}>{group.itemCount} 条收藏句子</span>
                </div>
                <button
                  type="button"
                  className={styles.studyBtn}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleStartSentenceStudy(group.bookId);
                  }}
                >
                  <span className={`material-icons-outlined text-sm ${styles.studyBtnIcon}`} aria-hidden="true">
                    school
                  </span>
                  去学习
                </button>
              </div>
            ))}
          </div>

 
        </div>
      </div>
    </div>
  );
};

export default StudySentencesPage;
